/**
 * The Provider Configuration dialog's roster — its master list (REQ-UI-156;
 * Flow V §4).
 *
 * Providers, not interfaces. Provider is the level the operator reasons at:
 * "stop collecting from the Black Diamond Range" is one act here and an unknown
 * number one level down.
 *
 * # Two counts, not a fraction
 *
 * Each row shows the provider's interface count **and** how many are started,
 * because one can be zero while the provider's own pill reads `started`: a
 * started provider with every interface stopped is collecting nothing, and a
 * bare "3 interfaces" would conceal exactly that. Neither count is privileged —
 * an interface's existence and state are in the all-roles field-set, while its
 * endpoints and filter literals are not.
 *
 * # The empty state is an alarm, not a blank
 *
 * *"No providers are configured. Nothing is feeding this deployment."* A
 * deployment with no peers is merely not forwarding; a deployment with no
 * providers is **not collecting**, and every downstream surface will render an
 * empty, healthy-looking picture. A **failed** load renders its own
 * ``role="alert"`` and is never mistaken for it — on ingress the difference
 * between "none configured" and "failed to load" is the difference between a
 * decision and an outage.
 *
 * # Absent, not disabled
 *
 * `Add provider` and `Import provider…` render only for `admin` /
 * `support_engineer` (REQ-UI-143). A disabled Add button tells an operator the
 * system has a capability they may not use, which on the surface that governs
 * what enters the deployment is an invitation to go looking for a way to use it.
 *
 * Cross-references:
 *   - REQ-UI-156 (the roster + its empty state), -157 (import is roster-level),
 *     -143 (absent-not-disabled).
 *   - ADR-040 §D-1; Flow V §3 / §4.
 */
import { StatePill } from "@/peers/peer-state-controls";
import type { ProviderView } from "@/providers/provider-model";
import { cn } from "@/lib/utils";

/** Props for {@link ProviderRoster}. */
export interface ProviderRosterProps {
  providers: readonly ProviderView[];
  selectedId: string | null;
  onSelect: (providerId: string) => void;
  /** Renders the create affordance. Absent — never disabled — for other roles. */
  mayMutate: boolean;
  onCreate: () => void;
  /**
   * Open the import dialog. Roster-level because importing CREATES a provider,
   * so it has no provider to belong to (REQ-UI-157). Absent — never disabled —
   * for a session that may not mutate, like `Add provider` beside it.
   */
  onImport: () => void;
  /** True while a create is in flight and its unsaved row should show. */
  creating: boolean;
  isLoading: boolean;
  error: string | null;
}

/** Render the provider roster. */
export function ProviderRoster({
  providers,
  selectedId,
  onSelect,
  mayMutate,
  onCreate,
  onImport,
  creating,
  isLoading,
  error,
}: ProviderRosterProps): React.ReactElement {
  return (
    <nav
      aria-label="Providers"
      data-testid="provider-roster"
      className="flex max-h-[35%] w-full flex-none flex-col border-b border-border bg-bg-base md:max-h-none md:w-[280px] md:border-b-0 md:border-r"
    >
      <div className="flex items-center gap-2 border-b border-border px-3 py-1.5">
        <span className="text-meta uppercase tracking-wide text-text-secondary">Providers</span>
        {mayMutate && (
          <div className="ml-auto flex items-center gap-1">
            <button
              type="button"
              onClick={onImport}
              data-testid="provider-import"
              title="Install a provider from a bundle on this workstation"
              className="rounded border border-border px-2 py-0.5 text-meta text-text-primary hover:bg-bg-elevated"
            >
              Import provider&hellip;
            </button>
            <button
              type="button"
              onClick={onCreate}
              data-testid="provider-add"
              className="rounded border border-border px-2 py-0.5 text-meta text-text-primary hover:bg-bg-elevated"
            >
              Add provider
            </button>
          </div>
        )}
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        {isLoading ? (
          <p className="px-3 py-2 text-body-sm text-text-secondary">Loading…</p>
        ) : error !== null ? (
          // Distinct from the empty state on purpose: an empty list and a list
          // that failed to load have opposite implications on an ingress
          // surface — one is a decision, the other is an outage.
          <p role="alert" className="px-3 py-2 text-body-sm text-destructive">
            Could not load providers: {error}
          </p>
        ) : providers.length === 0 && !creating ? (
          <p className="px-3 py-2 text-body-sm text-text-muted" data-testid="provider-roster-empty">
            No providers are configured. Nothing is feeding this deployment.
          </p>
        ) : (
          <ul>
            {providers.map((provider) => (
              <li key={provider.provider_id}>
                <ProviderRow
                  provider={provider}
                  selected={selectedId === provider.provider_id}
                  onSelect={() => onSelect(provider.provider_id)}
                />
              </li>
            ))}
            {creating && (
              <li
                data-testid="provider-row-unsaved"
                className="border-l-2 border-brand bg-bg-elevated px-3 py-2 text-body-sm text-text-primary"
              >
                New provider (unsaved)
              </li>
            )}
          </ul>
        )}
      </div>
    </nav>
  );
}

/** One roster row: name, configured state, and the two interface counts. */
function ProviderRow({
  provider,
  selected,
  onSelect,
}: {
  provider: ProviderView;
  selected: boolean;
  onSelect: () => void;
}): React.ReactElement {
  const configured = provider.interface_count;
  const started = provider.started_interface_count;
  // `undefined` (not resolved) and `[]` (declared none) both render nothing; only a
  // real declared identity earns a line.
  const nodes = provider.node_producers ?? [];
  const nodeNames = nodes.length > 0 ? nodes.map((n) => n.display_name).join(", ") : null;
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={selected ? "true" : undefined}
      data-testid={`provider-roster-row-${provider.name}`}
      className={cn(
        "flex w-full flex-col gap-1 border-l-2 px-3 py-2 text-left hover:bg-bg-elevated",
        selected ? "border-brand bg-bg-elevated" : "border-transparent",
      )}
    >
      <span className="flex items-center gap-2">
        <span className="min-w-0 flex-1 truncate text-body-sm text-text-primary">
          {provider.name}
        </span>
        <StatePill state={provider.state} />
      </span>
      <span
        className="text-meta text-text-secondary"
        data-testid={`provider-counts-${provider.name}`}
      >
        {configured === undefined || started === undefined
          ? // Never "0 interfaces" when the counts did not arrive — on this
            // surface a guessed zero reads as "nothing is arriving through this
            // provider", which is an alarm this row is in no position to raise.
            "Interface count unavailable"
          : `${configured} ${configured === 1 ? "interface" : "interfaces"} · ${started} started`}
      </span>
      {nodeNames !== null && (
        // "This provider's node is producer row X" (ADR-040 §D-1). The identity the
        // external system declared for itself at handshake — the row the Platforms
        // list deliberately excludes, surfaced here instead, which is the whole point
        // of excluding it there. Rendered only when there is one: a provider whose
        // interfaces carry no handshake has no node, and a line saying so on every
        // raw/CDS provider would be noise.
        <span
          className="truncate text-meta text-text-muted"
          data-testid={`provider-node-${provider.name}`}
          title={nodeNames}
        >
          Node: {nodeNames}
        </span>
      )}
    </button>
  );
}
